import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { motion, AnimatePresence, useMotionValue, useTransform } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";

// Global lightbox: any tappable image in the app can call openLightbox(src)
// with a single URL string OR an array of URL strings (+ optional start index)
// to enable swipe-between-photos.
const LightboxContext = createContext({ openLightbox: () => {}, closeLightbox: () => {} });

export function useLightbox() {
  return useContext(LightboxContext);
}

const DISMISS_THRESHOLD = 120;   // vertical drag → close
const SWIPE_THRESHOLD = 70;      // horizontal drag → prev/next

function LightboxView({ items, index, setIndex, onClose }) {
  // Track both axes — dragDirectionLock locks to whichever the user
  // commits to first (vertical = dismiss, horizontal = paginate).
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const bgOpacity = useTransform(y, [-300, 0, 300], [0, 1, 0]);
  const imgScale = useTransform(y, [-300, 0, 300], [0.82, 1, 0.82]);

  const src = items[index];
  const hasPrev = index > 0;
  const hasNext = index < items.length - 1;
  const multi = items.length > 1;

  const goPrev = useCallback(() => { if (hasPrev) setIndex(index - 1); }, [hasPrev, index, setIndex]);
  const goNext = useCallback(() => { if (hasNext) setIndex(index + 1); }, [hasNext, index, setIndex]);

  // Keyboard arrows for desktop browsing.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goPrev, goNext]);

  const onDragEnd = (_, info) => {
    const ax = Math.abs(info.offset.x);
    const ay = Math.abs(info.offset.y);
    const vy = Math.abs(info.velocity.y);
    const vx = Math.abs(info.velocity.x);
    // Vertical dominant → dismiss check
    if (ay > ax) {
      if (ay > DISMISS_THRESHOLD || vy > 500) {
        onClose();
        return;
      }
    } else {
      // Horizontal dominant → paginate
      if (ax > SWIPE_THRESHOLD || vx > 400) {
        if (info.offset.x < 0) goNext();
        else goPrev();
      }
    }
    // Snap back — motion handles spring return via the {x:0,y:0} animate prop.
    x.set(0);
    y.set(0);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.18 }}
      style={{ opacity: bgOpacity }}
      className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center touch-none"
      onClick={onClose}
      data-testid="lightbox-overlay"
    >
      {/* Desktop arrows — hidden on touch where the swipe gesture does the same job */}
      {multi && hasPrev && (
        <button
          onClick={(e) => { e.stopPropagation(); goPrev(); }}
          className="hidden sm:flex absolute left-4 z-10 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md items-center justify-center transition"
          aria-label="Previous"
          data-testid="lightbox-prev"
        >
          <ChevronLeft className="w-5 h-5 text-white" />
        </button>
      )}
      {multi && hasNext && (
        <button
          onClick={(e) => { e.stopPropagation(); goNext(); }}
          className="hidden sm:flex absolute right-4 z-10 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md items-center justify-center transition"
          aria-label="Next"
          data-testid="lightbox-next"
        >
          <ChevronRight className="w-5 h-5 text-white" />
        </button>
      )}

      <AnimatePresence mode="wait" initial={false}>
        <motion.img
          key={src}
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.96 }}
          transition={{ duration: 0.18 }}
          style={{ x, y, scale: imgScale }}
          drag
          dragDirectionLock
          dragElastic={1}
          dragMomentum={false}
          onDragEnd={onDragEnd}
          src={src}
          alt=""
          className="max-w-[100vw] max-h-[100vh] object-contain select-none cursor-grab active:cursor-grabbing"
          onClick={(e) => e.stopPropagation()}
          draggable={false}
          data-testid="lightbox-image"
        />
      </AnimatePresence>

      {/* Pagination dots + counter (only when there's more than one photo) */}
      {multi && (
        <div
          className="absolute bottom-[max(env(safe-area-inset-bottom),16px)] left-1/2 -translate-x-1/2 flex items-center gap-3"
          data-testid="lightbox-pagination"
        >
          <div className="flex items-center gap-1.5">
            {items.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all ${i === index ? "w-5 bg-white" : "w-1.5 bg-white/40"}`}
              />
            ))}
          </div>
          <span className="text-white/80 text-[11px] font-bold tracking-widest uppercase">
            {index + 1} / {items.length}
          </span>
        </div>
      )}
    </motion.div>
  );
}

export function LightboxProvider({ children }) {
  // null OR {items: string[], index: number}
  const [state, setState] = useState(null);

  const openLightbox = useCallback((srcOrItems, startIndex = 0) => {
    if (!srcOrItems) return;
    const items = Array.isArray(srcOrItems) ? srcOrItems.filter(Boolean) : [srcOrItems];
    if (items.length === 0) return;
    setState({ items, index: Math.max(0, Math.min(startIndex, items.length - 1)) });
  }, []);
  const closeLightbox = useCallback(() => setState(null), []);
  const setIndex = useCallback((i) => setState((s) => (s ? { ...s, index: i } : s)), []);

  useEffect(() => {
    if (!state) return undefined;
    const onKey = (e) => { if (e.key === "Escape") closeLightbox(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [state, closeLightbox]);

  return (
    <LightboxContext.Provider value={{ openLightbox, closeLightbox }}>
      {children}
      <AnimatePresence>
        {state && (
          <LightboxView
            items={state.items}
            index={state.index}
            setIndex={setIndex}
            onClose={closeLightbox}
          />
        )}
      </AnimatePresence>
    </LightboxContext.Provider>
  );
}

// Drop-in <img> wrapper that opens the lightbox on tap. Stops propagation so
// it can safely sit inside a <Link>, <button>, or any clickable row without
// triggering the parent's onClick. Tap the avatar → magnify. Tap anywhere
// else on the row → row action. Pass `photos` (array of URLs) to enable
// swipe-between-photos inside the lightbox.
export function Tappable({ src, photos, alt = "", className = "", expandSrc, testid, onClick, ...rest }) {
  const { openLightbox } = useLightbox();
  const handleClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (onClick) onClick(e);
    if (photos && photos.length > 0) {
      // Start on the photo that's visible if it matches one in the array.
      const startSrc = expandSrc || src;
      const startIdx = Math.max(0, photos.indexOf(startSrc));
      openLightbox(photos, startIdx);
    } else {
      openLightbox(expandSrc || src);
    }
  };
  return (
    <img
      src={src}
      alt={alt}
      className={`${className} cursor-zoom-in`}
      onClick={handleClick}
      data-testid={testid}
      {...rest}
    />
  );
}
